import React from 'react';
import Dialog from '../../../organisms/dialogs/Dialog/Dialog';
import SelectCustomizable from '../../../atoms/SelectCustomizable/SelectCustomizable';
import useDialogQuizLocation, { QuizLocationCourse } from './useDialogQuizLocation';

/**
 * DialogQuizLocation - Dialog to pick the module/submodule of the program where
 * a new quiz will be created (Home entry point, TAN-8587). Navigates to the quiz
 * creation page on confirm.
 */

interface DialogQuizLocationProps {
  course: QuizLocationCourse;
  isOpen: boolean;
  onClose: () => void;
}

function DialogQuizLocation({ course, isOpen, onClose }: DialogQuizLocationProps): JSX.Element {
  const {
    canCreate,
    hasContentError,
    hasSubmodules,
    isLoadingUnits,
    isModuleSelectDisabled,
    moduleError,
    moduleOptions,
    onChangeModule,
    onChangeSubmodule,
    onCreate,
    selectedModule,
    selectedSubmodule,
    submoduleOptions,
    t,
  } = useDialogQuizLocation({ course, isOpen, onClose });

  return (
    <Dialog
      buttonTextCancel={t('actions:Cancel')}
      buttonTextSuccess={t('course:quiz_location_create_quiz')}
      className="dialog-quiz-location"
      isDisabledSuccess={!canCreate}
      isOpen={isOpen}
      onCancel={onClose}
      onClose={onClose}
      onSuccess={onCreate}
      size="l"
      text={t('course:quiz_location_dialog_subtitle')}
      title={t('course:quiz_location_dialog_title')}
    >
      <div className="dialog-quiz-location__content">
        <div className="dialog-quiz-location__field">
          <SelectCustomizable
            aria-label={t('course:quiz_location_select_module')}
            error={moduleError}
            isClearable={false}
            isDisabled={isModuleSelectDisabled}
            isLoading={isLoadingUnits}
            isSearchable={false}
            onChange={onChangeModule}
            options={moduleOptions}
            placeholder={t('course:quiz_location_select_module')}
            value={selectedModule}
          />
        </div>

        {hasSubmodules && (
          <div className="dialog-quiz-location__field">
            <SelectCustomizable
              aria-label={t('course:quiz_location_select_submodule')}
              isClearable={false}
              isSearchable={true}
              onChange={onChangeSubmodule}
              options={submoduleOptions}
              placeholder={t('course:quiz_location_select_submodule')}
              value={selectedSubmodule}
            />
          </div>
        )}

        {hasContentError && (
          <div className="dialog-quiz-location__error">
            <div className="dialog-quiz-location__error-message">{t('course:quiz_location_content_unavailable_message')}</div>
          </div>
        )}
      </div>
    </Dialog>
  );
}

export default DialogQuizLocation;
